// Intl.NumberFormat construction is non-trivial (~ms) and these formatters
// are called inside render loops. Cache by argument signature.
const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + '|' + JSON.stringify(options);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Cents → user-facing dollar string.
 *
 * Uses Intl.NumberFormat so it picks up the current locale's grouping and
 * decimal separators. Always renders with currency symbol; use
 * {@link formatCents} for plain decimal output.
 */
export function formatCurrency(cents: number, currency: string = 'usd', locale: string = 'en-US'): string {
  return getFormatter(locale, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

/**
 * Like {@link formatCurrency}, but whole-dollar amounts drop the ".00"
 * ("$1,000" not "$1,000.00"); fractional amounts keep their cents. For
 * sentence copy (e.g. the deposit min/max messages) where trailing zero
 * cents read heavy.
 */
export function formatCurrencyWhole(cents: number, currency: string = 'usd', locale: string = 'en-US'): string {
  const fractionDigits = cents % 100 === 0 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {};
  return getFormatter(locale, { style: 'currency', currency: currency.toUpperCase(), ...fractionDigits }).format(
    cents / 100,
  );
}

/**
 * Dollars → integer cents. For API values that arrive in dollars (wallet
 * balance, Plaee portfolio values) before handing them to the cents-based
 * formatters above.
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * Cents → "12.50". No currency symbol. Useful as the initial value of an
 * `<input type="number">` where the user will type their own amount.
 */
export function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Cents → "1,000.00". Same as {@link formatCents} but with locale-aware
 * thousands separators. For text inputs that accept comma-grouped digits.
 */
export function formatCentsGrouped(cents: number, locale: string = 'en-US'): string {
  return getFormatter(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
}

/**
 * Restricts a raw amount keystroke string to a valid currency entry:
 * digits and a single "." with at most 2 fractional digits. Everything else
 * (letters, symbols, extra dots, grouping commas) is dropped. Intended for an
 * input's `onChange` so the field can never hold an un-parseable value.
 *
 * Only applied to live typing — programmatic values such as the comma-grouped
 * `formatCentsGrouped` output (quick-add chips, on-blur reformat) bypass this.
 */
export function sanitizeAmountInput(raw: string): string {
  // Keep only digits and dots.
  const digitsAndDots = raw.replace(/[^\d.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;
  // Keep the first dot; drop any subsequent dots from the fractional part.
  const intPart = digitsAndDots.slice(0, firstDot);
  const fracPart = digitsAndDots.slice(firstDot + 1).replace(/\./g, '');
  return intPart + '.' + fracPart.slice(0, 2);
}

/**
 * "12.50" → 1250 cents. Returns null if the input is empty, not a number,
 * or negative. Strips thousands separators so "1,000.00" parses correctly.
 * Always rounds to the nearest cent.
 */
export function parseDollarsToCents(input: string): number | null {
  const cleaned = input.replace(/,/g, '').trim();
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}
